import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Sun, LayoutDashboard, Users, Target, FileText, HardHat, Wrench, AlertTriangle, BarChart3, UserCog, LogOut, Settings as SettingsIcon, Zap, ReceiptText, ClipboardCheck, Wallet, ClipboardList, Truck, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Toaster } from "@/components/ui/sonner";
import NotificationBell from "@/components/crm/NotificationBell";

const NAV = [
  { to: "/dashboard", label: "Dashboard", hindi: "डैशबोर्ड", icon: LayoutDashboard, roles: ["admin"], perm: "dashboard" },
  { to: "/leads", label: "Leads", hindi: "लीड्स", icon: Target, roles: ["admin","sales"], perm: "leads" },
  { to: "/customers", label: "Customers", hindi: "ग्राहक", icon: Users, roles: ["admin","sales"], perm: "customers" },
  { to: "/quotations", label: "Quotations", hindi: "कोटेशन", icon: FileText, roles: ["admin","sales"], perm: "quotations" },
  { to: "/tax-invoices", label: "Tax Invoices", hindi: "टैक्स इनवॉइस", icon: ReceiptText, roles: ["admin"], perm: "tax_invoices" },
  { to: "/payments", label: "Payments", hindi: "भुगतान", icon: Wallet, roles: ["admin","sales"], perm: "payments" },
  { to: "/material-requisition", label: "Material Req.", hindi: "मटेरियल रिक्वेस्ट", icon: ClipboardList, roles: ["admin","sales"], perm: "mrn" },
  { to: "/hisaab", label: "Hisaab", hindi: "हिसाब", icon: Calculator, roles: ["admin","sales"], perm: "hisaab" },
  { to: "/projects", label: "Projects", hindi: "प्रोजेक्ट", icon: HardHat, roles: ["admin"], perm: "projects" },
  { to: "/work-completion", label: "Work Completion", hindi: "वर्क कम्प्लीशन", icon: ClipboardCheck, roles: ["admin","sales"], perm: "wcr" },
  { to: "/services", label: "Service", hindi: "सर्विस", icon: Wrench, roles: ["admin","technician"], perm: "services" },
  { to: "/complaints", label: "Complaints", hindi: "शिकायतें", icon: AlertTriangle, roles: ["admin","technician"], perm: "complaints" },
  { to: "/reports", label: "Reports", hindi: "रिपोर्ट", icon: BarChart3, roles: ["admin"], perm: "reports" },
  { to: "/users", label: "Users", hindi: "यूज़र", icon: UserCog, roles: ["admin"], perm: "users" },
  { to: "/integrations", label: "Integrations", hindi: "इंटीग्रेशन", icon: Zap, roles: ["admin"], perm: "integrations" },
  { to: "/settings", label: "Settings", hindi: "सेटिंग", icon: SettingsIcon, roles: ["admin"], perm: "settings" },
];

// Fallback per-role defaults for menu visibility if the user has no explicit permissions map.
const ROLE_DEFAULTS = {
  admin:      { dashboard:true, leads:true, customers:true, quotations:true, tax_invoices:true, payments:true, mrn:true, hisaab:true, projects:true, wcr:true, services:true, complaints:true, reports:true, users:true, settings:true, integrations:true },
  sales:      { leads:true, customers:true, quotations:true, payments:true, mrn:true, hisaab:true, wcr:false },
  technician: { services:true, complaints:true },
};

// Priority: explicit user.permissions (true=grant, false=deny) > role defaults from ROLE_DEFAULTS > NAV.roles restriction.
function canAccess(user, item) {
  const explicit = user?.permissions?.[item.perm];
  if (explicit === true) return true;
  if (explicit === false) return false;
  const roleDefault = ROLE_DEFAULTS[user?.role]?.[item.perm];
  if (roleDefault === false) return false;
  return item.roles.includes(user?.role);
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [company, setCompany] = useState(null);

  useEffect(() => {
    api.get("/settings/company").then(({ data }) => setCompany(data)).catch(() => {});
  }, []);

  // 1) Role gate  2) Per-user permission override (falls back to role defaults, then allow).
  const items = NAV.filter(n => canAccess(user, n));

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="mrm-sidebar w-64 hidden md:flex flex-col shrink-0" data-testid="sidebar">
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            {company?.logo_url ? (
              <img src={company.logo_url} alt="MRM" className="h-11 w-11 object-contain bg-white rounded-xl p-1" data-testid="sidebar-logo" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
                <Sun className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
            )}
            <div>
              <div className="text-white font-bold text-lg tracking-tight leading-none" style={{fontFamily:'Outfit'}}>MRM</div>
              <div className="text-amber-400/80 text-[10px] uppercase tracking-widest font-medium">Renewables</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {items.map((n) => {
            const Icon = n.icon;
            const active = loc.pathname.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.slice(1)}`}
                className={`mrm-sidebar-item flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${active ? "active" : ""}`}
              >
                <Icon className="w-4 h-4" />
                <div className="flex-1">
                  <div>{n.label}</div>
                  <div className="text-[10px] opacity-60 leading-tight">{n.hindi}</div>
                </div>
              </Link>
            );
          })}
        </nav>
        <div className="px-4 py-3 border-t border-white/10 text-[11px] text-slate-400">
          {company?.gstin ? `GSTIN: ${company.gstin}` : "MRM CRM · v1.0"}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 no-print sticky top-0 z-20">
          <div>
            <div className="text-slate-900 font-semibold text-sm" style={{fontFamily:'Outfit'}}>
              {company?.company_name || "MRM Renewables CRM"}
            </div>
            <div className="text-slate-500 text-[11px]">Solar Sales & Service Management</div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="hidden sm:flex items-center px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200">
              <span className="text-[10px] font-semibold text-amber-800 uppercase tracking-wider">
                {user?.role}
              </span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="user-menu-btn">
                  {user?.name || user?.email}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-slate-500">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-menu">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
        <Toaster position="top-right" richColors />
      </div>
    </div>
  );
}
